/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';
import * as Highcharts from 'highcharts';
import {Subscription} from 'rxjs';

import template from './stats-widget.component.html';

import {StatsApiService} from '../../common/controller/stats-api.service';
import {OBJECT_TYPE} from '../../common/constants/object-type';
import {applyHighchartsLocale, chunkYValue} from './workspace-widget-chart-commons';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {AppLocaleService} from '../../common/locale/app-locale.service';
import {getIntlLocaleData} from '../../common/locale/intl-locale-data';
import {ChartOptionsUtil} from '../../common/options/chart-options-util.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {deferSignalStateWrite} from '../../common/util/template-state';

@Component({
  standalone: false,
  selector: 'adn-bar-chart-widget',
  template
})
export class BarChartWidgetComponent implements OnInit, OnDestroy {
  @Input() widgetType: any;

  @ViewChild('chartContainer')
  set chartContainer(chartContainer: ElementRef<HTMLElement>) {
    this.chartContainerRef = chartContainer;
    this.renderChart();
  }

  readonly chartTitle = signal('');
  readonly error = signal(false);
  readonly isPending = signal(false);
  readonly haveStats = signal(false);
  readonly noData = signal<string | null>(null);

  private chart: Highcharts.Chart | null = null;
  private chartContainerRef: ElementRef<HTMLElement> | null = null;
  private chartOptions: any = null;
  private networkTimezoneOffset = 0;
  private statsSubscription?: Subscription;

  constructor(
    private readonly statsApi: StatsApiService,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    private readonly translate: TranslateService,
    private readonly adnLog: AdnLogService,
    private readonly appLocale: AppLocaleService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any
  ) {
  }

  ngOnInit(): void {
    const timezone = this.LocalNetworkProfile.get('timeZone');
    this.networkTimezoneOffset = moment.tz(timezone).utcOffset();

    let dateOption = this.ChartOptionsUtil.getDateOptions().last7Days;
    if (this.widgetType.dateOption && this.ChartOptionsUtil.getDateOptions()[this.widgetType.dateOption]) {
      dateOption = this.ChartOptionsUtil.getDateOptions()[this.widgetType.dateOption];
    }
    const startDate = dateOption.startDate();
    const endDate = dateOption.endDate();
    this.chartTitle.set('Data for ' + this.translate.instant('chart.dateOption.' + dateOption.id + '.sentence'));

    const ids = _.map(this.widgetType.objIds, 'id');
    if (!ids.length) {
      this.isPending.set(false);
      this.noData.set('Select at least one object to report stats on.');
      return;
    }

    const objType = OBJECT_TYPE[this.widgetType.objectType] || OBJECT_TYPE.LineItem;
    const chartType = this.ChartOptionsUtil.getOptions(objType.caps);

    const setUpFieldData = (theField: string) => {
      const networkTrans = this.LocalNetworkProfile.getNetworkTranslations(theField);
      return {
        name: networkTrans || this.translate.instant('report.field.' + theField),
        data: [],
        adnId: theField
      };
    };

    this.chartOptions = {
      chart: {
        type: 'bar',
        height: this.widgetType.height || 300
      },
      xAxis: {
        categories: [],
        title: {text: null}
      },
      tooltip: {shared: true},
      series: [],
      title: {text: null},
      credits: {enabled: false},
      accessibility: {enabled: false},
      exporting: {enabled: true}
    };

    const statsParams: any = {
      id: _.slice(ids, 0, 15).join(';'),
      idKey: chartType.idKey,
      startDate,
      endDate,
      groupBy: objType.caps
    };

    this.statsSubscription = this.statsApi.get(statsParams).subscribe({next: (stats: any) => {
      applyHighchartsLocale(getIntlLocaleData(this.appLocale.getLocaleId()), this.networkTimezoneOffset);
      this.error.set(false);

      const fieldsOfJoy: any[] = [];
      _.forEach(['', '2', '3', '4', '5', '6', '7'], (suffix) => {
        if (this.widgetType['selField' + suffix]) {
          fieldsOfJoy.push(setUpFieldData(this.widgetType['selField' + suffix]));
        }
      });

      this.chartOptions.xAxis.categories = [];
      _.forEach(stats.chunks, (chunk: any) => {
        _.forEach(fieldsOfJoy, (fieldData) => {
          const dataPoint = chunk[fieldData.adnId];
          fieldData.data.push(chunkYValue(dataPoint) || 0);
        });
        this.chartOptions.xAxis.categories.push(chunk[objType.apiParam].name);
      });
      this.chartOptions.series = fieldsOfJoy;

      const noData = !stats.chunks.length
        ? 'No data was found for your selected '
          + this.translate.instant('object.' + this.widgetType.objectType + '.plural').toLowerCase()
        : null;
      deferSignalStateWrite(() => {
        this.noData.set(noData);
        this.haveStats.set(true);
        this.isPending.set(false);
        this.renderChart();
      });
    }, error: (data: any) => {
      this.adnLog.warn(data);
      deferSignalStateWrite(() => {
        if (data.status !== -1) {
          this.error.set(true);
        }
        this.isPending.set(false);
      });
    }});
  }

  ngOnDestroy(): void {
    this.statsSubscription?.unsubscribe();
    this.chart?.destroy();
    this.chart = null;
  }

  private renderChart(): void {
    if (!this.chartContainerRef?.nativeElement || !this.chartOptions || !this.haveStats() || this.noData()) {
      return;
    }
    this.chart?.destroy();
    this.chart = Highcharts.chart(this.chartContainerRef.nativeElement, this.chartOptions);
  }
}
